import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Mail, Search, RefreshCw, Loader2, Eye, ChevronLeft, ChevronRight,
  CheckCircle2, XCircle, Paperclip,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_META = {
  daily_update: { label: 'Daily Update', cls: 'bg-indigo-100 text-indigo-700' },
  coupon: { label: 'Coupon', cls: 'bg-amber-100 text-amber-700' },
  marketing: { label: 'Marketing', cls: 'bg-purple-100 text-purple-700' },
  approval: { label: 'Approval', cls: 'bg-emerald-100 text-emerald-700' },
  job_notification: { label: 'Job Notification', cls: 'bg-sky-100 text-sky-700' },
  tracking: { label: 'Tracking', cls: 'bg-cyan-100 text-cyan-700' },
  report: { label: 'Report', cls: 'bg-blue-100 text-blue-700' },
  account: { label: 'Account', cls: 'bg-slate-200 text-slate-700' },
  notification: { label: 'Notification', cls: 'bg-gray-200 text-gray-700' },
  reminder: { label: 'Reminder', cls: 'bg-orange-100 text-orange-700' },
  other: { label: 'Other', cls: 'bg-gray-100 text-gray-600' },
};

const CategoryBadge = ({ category }) => {
  const meta = CATEGORY_META[category] || CATEGORY_META.other;
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${meta.cls}`}>
      {meta.label}
    </span>
  );
};

const StatusBadge = ({ status }) => (
  status === 'sent' ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
      <CheckCircle2 size={12} /> Sent
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
      <XCircle size={12} /> Failed
    </span>
  )
);

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso.endsWith('Z') ? iso : iso + 'Z').toLocaleString('en-US', {
      timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    }) + ' CT';
  } catch {
    return iso;
  }
};

export default function AdminEmailLogs() {
  const { getAuthHeader } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const limit = 25;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search) params.search = search;
      if (category) params.category = category;
      if (status) params.status = status;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await axios.get(`${API}/admin/email-logs`, { ...getAuthHeader(), params });
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load email logs');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, category, status, dateFrom, dateTo]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail({ id });
    try {
      const res = await axios.get(`${API}/admin/email-logs/${id}`, getAuthHeader());
      setDetail(res.data);
    } catch (e) {
      toast.error('Failed to load email detail');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const applySearch = () => { setPage(1); setSearch(searchInput.trim()); };
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const selectCls = 'h-9 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500';

  return (
    <div className="p-6" data-testid="admin-email-logs-page">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
          <Mail className="text-purple-600" size={20} />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-800">Email Log</h2>
          <p className="text-sm text-gray-500">History of every email sent to clients — daily updates, coupons, approvals and more.</p>
        </div>
      </div>

      <Card className="mt-4 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="text-xs font-medium text-gray-500 mb-1 block">Search recipient or subject</label>
            <div className="flex gap-2">
              <Input
                data-testid="email-logs-search-input"
                placeholder="e.g. client@example.com"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applySearch()}
                className="h-9"
              />
              <Button data-testid="email-logs-search-btn" size="sm" variant="outline" className="h-9" onClick={applySearch}>
                <Search size={15} />
              </Button>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Type</label>
            <select data-testid="email-logs-category-filter" className={selectCls} value={category}
              onChange={(e) => { setPage(1); setCategory(e.target.value); }}>
              <option value="">All types</option>
              {Object.entries(CATEGORY_META).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Status</label>
            <select data-testid="email-logs-status-filter" className={selectCls} value={status}
              onChange={(e) => { setPage(1); setStatus(e.target.value); }}>
              <option value="">All</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">From date</label>
            <input data-testid="email-logs-date-from" type="date" className={selectCls} value={dateFrom}
              onChange={(e) => { setPage(1); setDateFrom(e.target.value); }} />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">To date</label>
            <input data-testid="email-logs-date-to" type="date" className={selectCls} value={dateTo}
              onChange={(e) => { setPage(1); setDateTo(e.target.value); }} />
          </div>
          <Button data-testid="email-logs-refresh-btn" size="sm" variant="outline" className="h-9" onClick={fetchLogs}>
            <RefreshCw size={15} />
          </Button>
        </div>
      </Card>

      <Card className="mt-4 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 className="animate-spin mr-2" size={20} /> Loading email logs…
          </div>
        ) : logs.length === 0 ? (
          <div className="py-16 text-center text-gray-400" data-testid="email-logs-empty-state">
            <Mail size={32} className="mx-auto mb-2 opacity-40" />
            No emails found for the selected filters.
            <p className="text-xs mt-1">Emails are logged from the moment this feature was installed — older sends are not shown.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="email-logs-table">
              <thead>
                <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wide">
                  <th className="px-4 py-3">Sent</th>
                  <th className="px-4 py-3">To</th>
                  <th className="px-4 py-3">Subject</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} className="border-t hover:bg-purple-50/40 transition-colors" data-testid={`email-log-row-${log.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">{fmtDate(log.created_at)}</td>
                    <td className="px-4 py-3 text-gray-800 max-w-[220px] truncate" title={(log.to || []).join(', ')}>
                      {(log.to || []).join(', ') || '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-700 max-w-[320px] truncate" title={log.subject}>
                      <span className="inline-flex items-center gap-1">
                        {log.subject || '(no subject)'}
                        {log.attachment_count > 0 && (
                          <span className="inline-flex items-center text-gray-400 text-xs"><Paperclip size={12} />{log.attachment_count}</span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3"><CategoryBadge category={log.category} /></td>
                    <td className="px-4 py-3"><StatusBadge status={log.status} /></td>
                    <td className="px-4 py-3 text-right">
                      <Button data-testid={`email-log-view-btn-${log.id}`} size="sm" variant="outline"
                        onClick={() => openDetail(log.id)}>
                        <Eye size={14} className="mr-1" /> View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50 text-sm text-gray-600">
            <span data-testid="email-logs-total-count">{total} email{total === 1 ? '' : 's'} · Page {page} of {totalPages}</span>
            <div className="flex gap-2">
              <Button data-testid="email-logs-prev-page" size="sm" variant="outline" disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}>
                <ChevronLeft size={15} /> Prev
              </Button>
              <Button data-testid="email-logs-next-page" size="sm" variant="outline" disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}>
                Next <ChevronRight size={15} />
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Dialog open={!!detail} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="email-log-detail-dialog">
          <DialogHeader>
            <DialogTitle className="pr-8">{detail?.subject || 'Email detail'}</DialogTitle>
            <DialogDescription>Full record of this outbound email.</DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex items-center justify-center py-10 text-gray-400">
              <Loader2 className="animate-spin mr-2" size={18} /> Loading…
            </div>
          ) : detail && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div><span className="text-gray-500">To:</span> <span className="text-gray-800" data-testid="email-detail-to">{(detail.to || []).join(', ') || '—'}</span></div>
                <div><span className="text-gray-500">From:</span> <span className="text-gray-800">{detail.from_email || '—'}</span></div>
                {(detail.bcc || []).length > 0 && (
                  <div><span className="text-gray-500">BCC:</span> <span className="text-gray-800">{detail.bcc.join(', ')}</span></div>
                )}
                <div><span className="text-gray-500">Sent:</span> <span className="text-gray-800">{fmtDate(detail.created_at)}</span></div>
                <div className="flex items-center gap-2"><span className="text-gray-500">Type:</span> <CategoryBadge category={detail.category} /></div>
                <div className="flex items-center gap-2"><span className="text-gray-500">Status:</span> <StatusBadge status={detail.status} /></div>
              </div>
              {detail.status === 'failed' && detail.error && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm" data-testid="email-detail-error">
                  {detail.error}
                </div>
              )}
              {detail.body_html ? (
                <div className="border rounded-md overflow-hidden bg-gray-50">
                  <iframe
                    title="Email body"
                    sandbox=""
                    srcDoc={detail.body_html}
                    className="w-full bg-white"
                    style={{ height: '55vh', border: 'none' }}
                    data-testid="email-detail-body-iframe"
                  />
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic">Body was not captured for this send.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
